import { Autocomplete, TextField, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { CHECK_EMAIL, SIGNUP, USERNAME } from "../../constants/api";
import Button from "../../components/Button";
import BottomText from "../../components/BottomText";
import axios from "../../MyAxios";
import { useNavigate } from "react-router-dom";
import mentee from "@images/logo.png";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from "dayjs";
import SignupAutocomplete from "@components/SignupAutocomplete";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 300,
  bgcolor: "background.paper",
  borderRadius: "10px",
  boxShadow: 24,
  p: 4,
};

const useStyles = makeStyles((theme) => ({
  border: {
    borderWidth: 1,
    borderColor: "#887E7E !important",
  },
  textColor: {
    color: theme.palette.primary.main,
  },
  button: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
  },
  fontSmall: {
    fontSize: "10px",
  },
  bottomImg: {
    position: "fixed",
    bottom: 0,
    right: 0,
    left: 0,
  },
}));

const SignupDetails = (res) => {
  const classes = useStyles();
  const navigation = useNavigate();
  const { user } = useSelector((state) => state.user);
  const [responseError, setResponseError] = useState("");

  const [totalCountries, setTotalCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm();

  const getGeoData = () => {
    axios.get('/user/get-geo-data', {
      params: {
        timezone: watch('timezone')
      }
    })
      .then(res => {
        if(res.data?.error) {
          console.log(res.data);
          return;
        }

        if(res.data.allCountries)
          setTotalCountries(res.data.allCountries);

        if(res.data.autoSelectedCountry && !watch('country'))
          setValue('country', res.data.autoSelectedCountry);
      })
      .catch(err => {
        console.log(err);
      })
  }

  const getGeoStates = () => {
    if(typeof watch('country') === 'string')
      return;

    axios.get('/user/get-country-geo-states', {
      params: {
        country: watch('country')?.name
      }
    })
      .then(res => {
        if(res.data?.error) {
          console.log(res.data);
          return;
        }

        if(res.data.states)
          setStates(res.data.states);
      })
      .catch(err => {
        console.log(err);
      })
  }

  const getGeoCities = () => {
    if(typeof watch('state') === 'string')
      return;

    axios.get('/user/get-state-geo-cities', {
      params: {
        stateId: watch('state')?._id
      }
    })
      .then(res => {
        if(res.data?.error) {
          console.log(res.data);
          return;
        }

        if(res.data.cities)
          setCities(res.data.cities);
      })
      .catch(err => {
        console.log(err);
      })
  }

  useEffect(() => {
    // Retrieve form data from localStorage and set state
    const storedFormData = JSON.parse(localStorage.getItem("formData")) || {};
    setValue("userName", user || storedFormData.userName);
    setValue("emailAddress", storedFormData.email || "");
    setValue("phoneNumber", storedFormData.phone || "");
    setValue("firstName", storedFormData.firstName || "");
    setValue("lastName", storedFormData.lastName || "");
    setValue("aliasOne", storedFormData.aliasOne || "");
    setValue("aliasTwo", storedFormData.aliasTwo || "");
    setValue("age", storedFormData.age || "");
    setValue("dateOfBirth", storedFormData.dateOfBirth || null);
    setValue("country", storedFormData.country || null);
    setValue("state", storedFormData.state || null);
    setValue("city", storedFormData.city || null);
    setValue("timezone", storedFormData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || '');
    getGeoData();
  }, []);

  // Form submission handler
  const onSubmit = async (data) => {
    if(!data.country)
      return setError('country', { message: 'Please select a country' });

    if(!data.state)
      return setError('state', { message: 'Please select a state' });

    const formData = {
      userName: watch("userName"),
      phone: watch("phoneNumber"),
      firstName: watch("firstName"),
      lastName: watch("lastName"),
      aliasOne: watch("aliasOne"),
      aliasTwo: watch("aliasTwo"),
      email: watch("emailAddress"),
      age: watch("age"),
      dateOfBirth: watch("dateOfBirth"),
      country: watch("country")?.name,
      state: watch("state")?.name,
      city: watch("city")?.name || '',
      timezone: watch("timezone"),
    };
    localStorage.setItem("formData", JSON.stringify(formData));

    try {
      const res = await axios.post(CHECK_EMAIL, data);
      navigation(SIGNUP);
    } catch (error) {
      console.log(error);
      setResponseError(error.response.data);
    }
  };
  const navigate = useNavigate();

  const validatePhoneNumber = (value) => {
    if (!value || value === "") return true; // Allow empty input (optional field)
    const phoneNumberDigits = value.replace(/\D/g, ""); // Remove non-digits
    if (!/^\d+$/.test(phoneNumberDigits)) {
      // Check if contains only digits
      return "Phone number must contain only digits";
    }
    if (phoneNumberDigits.length !== 10) {
      // Check if length is 10
      return "Phone number must be 10 digits";
    }
    return true; // Validation passed
  };

  useEffect(() => {
    if(totalCountries?.length && watch('country') && typeof watch('country') === 'string') {
      const selectedCountry = totalCountries.find(country => country.name === watch('country'));
      setValue('country', selectedCountry);
    }

    if(states?.length && watch('state') && typeof watch('state') === 'string') {
      const selectedState = states.find(state => state.name === watch('state'));
      setValue('state', selectedState);
    }

    if(cities?.length && watch('city') && typeof watch('city') === 'string') {
      const selectedCity = cities.find(city => city.name === watch('city'));
      setValue('city', selectedCity);
    }

  }, [totalCountries, states, cities, watch('country'), watch('state'), watch('city')]);

  useEffect(() => {
    if(watch('country')) {
      getGeoStates();
      clearErrors('country');
    }

    if(typeof watch('state') !== 'string') {
      setValue('state', null);
      setStates([]);
    }
  }, [watch('country')]);

  useEffect(() => {
    if(watch('state')) {
      getGeoCities();
      clearErrors('state');
    }

    if(typeof watch('city') !== 'string') {
      setValue('city', null);
      setCities([]);
    }
  }, [watch('state')]);

  useEffect(() => {
    if(watch('city')) {
      clearErrors('city');
    }

  }, [watch('city')]);

  return (
    <div className="m-auto flex w-5/6 flex-col gap-4 md:w-3/12">
      <div>
        <Typography
          variant="h4"
          className={clsx(classes.textColor, " text-center font-extrabold mt-4")}
        >
          Sign Up for
        </Typography>
        <img
          src={mentee}
          alt="mentee"
          className="mx-auto mb-2"
          style={{ height: "30px" }}
        />
      </div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        encType="multipart/form-data"
        className="flex flex-col gap-2"
      >
        <div className=" flex flex-col">
          <label className="ml-1">
            <small>Email *</small>
          </label>
          <input
            className={clsx(
              classes.border,
              "rounded-md p-3",
              res.message ? "border-red-400" : ""
            )}
            placeholder="Email"
            type="email"
            {...register("emailAddress", {
              required: "Email is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]+$/i,
                message: "Invalid email address",
              },
            })}
          />
          {errors.emailAddress && (
            <p className="text-xs italic text-red-500">
              {errors.emailAddress.message}
            </p>
          )}
        </div>

        <div className=" flex flex-col">
          <label className="ml-1">
            <small>First Name *</small>
          </label>
          <input
            className={clsx(classes.border, "rounded-md p-3")}
            placeholder="First Name"
            type="text"
            {...register("firstName", {
              required: "First Name is required",
              minLength: {
                value: 3,
                message: "First Name must be at least 3 characters",
              },
            })}
          />
          {errors.firstName && (
            <p className="text-xs italic text-red-500">
              {errors.firstName.message}
            </p>
          )}
        </div>

        <div className=" flex flex-col">
          <label className="ml-1">
            <small>Last Name *</small>
          </label>
          <input
            className={clsx(classes.border, "rounded-md p-3")}
            placeholder="Last Name"
            type="text"
            {...register("lastName", {
              required: "Last Name is required",
              minLength: {
                value: 3,
                message: "Last Name must be at least 3 characters",
              },
            })}
          />
          {errors.lastName && (
            <p className="text-xs italic text-red-500">
              {errors.lastName.message}
            </p>
          )}
        </div>
        <div className="input-wrapper flex flex-col">
          <label className="ml-1">
            <small>Date of Birth *</small>
          </label>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              maxDate={dayjs()}
              value={watch('dateOfBirth') ? dayjs(watch('dateOfBirth'), 'MM/DD/YYYY') : null}
              onChange={(newDate) => setValue('dateOfBirth', newDate.format('MM/DD/YYYY'))}
              slotProps={{
                textField: {
                  InputProps: {
                    classes: {
                      notchedOutline: clsx(classes.border, "rounded-md p-3"),
                    },
                  }
                }
              }}
            />
          </LocalizationProvider>
          {errors.dateOfBirth && (
            <p className="text-xs italic text-red-500">{errors.dateOfBirth.message}</p>
          )}
        </div>

        <div className="input-wrapper flex flex-col">
          <label className="ml-1">
            <small>Phone Number</small>
          </label>
          <input
            className={clsx(classes.border, "rounded-md p-3")}
            placeholder="(XXX) XXX-XXXX"
            type="text"
            {...register("phoneNumber", {
              validate: validatePhoneNumber,
              required: false,
            })}
          />
          {errors.phoneNumber && (
            <p className="text-xs italic text-red-500">
              {errors.phoneNumber.message}
            </p>
          )}
        </div>

        <div className="input-wrapper flex flex-col">
          <label className="ml-1">
            <small>Country *</small>
          </label>
          <SignupAutocomplete
            options={totalCountries || []}
            getOptionLabel={(option) => option.name}
            name = 'country'
            value={watch("country") ?? null}
            onChange={setValue}
          />
          {errors.country && (
            <p className="text-xs italic text-red-500">{errors.country.message}</p>
          )}
        </div>

        <div className="input-wrapper flex flex-col">
          <label className="ml-1">
            <small>State/Region *</small>
          </label>
          <SignupAutocomplete
            options={states || []}
            getOptionLabel={(option) => option.name}
            name = 'state'
            value={watch("state") ?? null}
            onChange={setValue}
          />
          {errors.state && (
            <p className="text-xs italic text-red-500">{errors.state.message}</p>
          )}
        </div>
        <div className="input-wrapper flex flex-col">
          <label className="ml-1">
            <small>City *</small>
          </label>
          <SignupAutocomplete
            options={cities || []}
            getOptionLabel={(option) => option.name}
            name = 'city'
            value={watch("city") ?? null}
            onChange={setValue}
          />
          {errors.city && (
            <p className="text-xs italic text-red-500">{errors.city.message}</p>
          )}
        </div>

        <Button>Next</Button>
      </form>
    </div>
  );
};

export default SignupDetails;
